import { randomUuidCompat } from "../../common/utils/randomUuidCompat";

export type LogLevel = "debug" | "info" | "warn" | "error" | "fatal";

export interface LogEntry {
	id: string;
	level: LogLevel;
	msg: string;
	source: string;
	timestamp: string;
	sessionId: string;
	appVersion: string;
	offline: boolean;
}

export interface LoggerMetadata {
	userAgent: string;
	appVersion: string;
	appPlatform: "browser" | "mobile";
	screenResolution: string;
	timezone: string;
	networkType: string;
	deviceInfo?: {
		name?: string;
		model?: string;
		platform?: string;
		operatingSystem?: string;
		osVersion?: string;
		iOSVersion?: number;
		androidSDKVersion?: number;
		manufacturer?: string;
		memUsed?: number;
		webViewVersion?: string;
	};
}

interface BrowserLoggerOptions {
	sessionId: string;
	source: string;
	metadata: LoggerMetadata;
	enableConsoleLog: boolean;
	minLevel: LogLevel;
	enabled: boolean;
	onLog?: (entry: LogEntry, meta: LoggerMetadata) => void;
	flush?: () => Promise<unknown>;
}

export class BrowserLogger {
	private readonly levelPriority: Record<LogLevel, number> = {
		debug: 0,
		info: 1,
		warn: 2,
		error: 3,
		fatal: 4,
	};

	constructor(private options: BrowserLoggerOptions) {}

	private shouldLog(level: LogLevel): boolean {
		if (!this.options.enabled) return false;
		if (!this.options.minLevel) return true;
		return (
			this.levelPriority[level] >= this.levelPriority[this.options.minLevel]
		);
	}

	public log(level: LogLevel, msg: string) {
		if (!this.shouldLog(level)) return;
		const entry: LogEntry = {
			id: randomUuidCompat(),
			level,
			msg: msg.length > 2000 ? msg.slice(0, 2000) : msg,
			source: this.options.source,
			timestamp: new Date().toISOString(),
			sessionId: this.options.sessionId,
			appVersion: this.options.metadata.appVersion,
			offline: navigator.onLine === false,
		};

		this.options.onLog?.(entry, this.options.metadata);

		if (this.options.enableConsoleLog) {
			const consoleMethod = level === "fatal" ? "error" : level;
			console[consoleMethod](`[${level.toUpperCase()}]`, msg);
		}
	}

	public async flush() {
		await this.options.flush?.();
	}

	public debug(msg: string) {
		this.log("debug", msg);
	}

	public info(msg: string) {
		this.log("info", msg);
	}

	public warn(msg: string) {
		this.log("warn", msg);
	}

	public error(msg: string) {
		this.log("error", msg);
	}

	public fatal(msg: string) {
		this.log("fatal", msg);
	}

	public setDeviceInfo(deviceInfo: LoggerMetadata["deviceInfo"]) {
		this.options.metadata.deviceInfo = deviceInfo;
	}
}
